import { Button, Flex, IconButton } from '@brightdrop/bd-ui';
import { TranslatedLabel } from '@gm-commercial/translations';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMediaQuery, useTheme } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import CloseIcon from '../../assets/icons/close.svg?react';
import {
  BANNER_SKELETON_HEIGHT,
  GLOBAL_HEADER_HEIGHT,
  TABLE_STICKY_HEADER_TOP_VAR,
  UNINITIALIZED_POSITION,
  VIEW_AS_MOBILE_BANNER_HEIGHT,
} from '../../constants';
import type {
  BannerAction,
  TableBannerPropsWithRef,
} from '../../models/selectedRowActionsBanner.model';
import useStyles, {
  SELECTED_ROW_ACTION_BANNER_Z_INDEX,
} from './SelectedRowActionsBanner.styles';
import {
  renderButtonAction,
  renderLabel,
} from './SelectedRowActionsBannerRenderers';

/**
 * Returns true when the MRT table container has a constrained height so sticky
 * headers stick inside that scrollport (not the viewport).
 */
const hasContainedTableScroll = (table: HTMLElement): boolean => {
  const container = table.querySelector(
    '.MuiTableContainer-root'
  ) as HTMLElement | null;
  if (!container) {
    return false;
  }

  const { maxHeight, height, overflowY } = getComputedStyle(container);

  const isConstrained = (value: string): boolean =>
    Boolean(
      value &&
        value !== 'none' &&
        value !== 'unset' &&
        value !== 'auto' &&
        value !== '0px'
    );

  const scrollsY =
    overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';

  return scrollsY && (isConstrained(maxHeight) || isConstrained(height));
};

/**
 * Sets or clears the sticky-header top offset CSS variable on the table root
 * when the selection banner is viewport-pinned on a page-scroll table.
 * Contained-scroll tables clear the var: headers stick inside the table
 * scrollport while the pinned banner (and its spacer) sit outside it.
 */
const syncStickyHeaderTopVar = ({
  table,
  isPinned,
  topOffset,
  bannerHeight,
  isContainedScroll,
}: {
  table: HTMLElement | null;
  isPinned: boolean;
  topOffset: number;
  bannerHeight: number;
  isContainedScroll: boolean;
}): void => {
  if (!table) {
    return;
  }
  if (isPinned && bannerHeight > 0 && !isContainedScroll) {
    table.style.setProperty(
      TABLE_STICKY_HEADER_TOP_VAR,
      `${topOffset + bannerHeight}px`
    );
  } else {
    table.style.removeProperty(TABLE_STICKY_HEADER_TOP_VAR);
  }
};

const SelectedRowActionsBanner = ({
  tableRef,
  selectedCounter,
  formattedSelectedCount,
  isOpen,
  onCloseBanner,
  actions,
  isLoading = false,
  selectAllConfig,
  csvExportConfig,
  isAdvisorActing = false,
  disableViewportSticky = false,
}: TableBannerPropsWithRef) => {
  const { classes } = useStyles();
  const theme = useTheme();
  // Match GlobalHeader: View-as is inline in the header from `md` up; below that
  // it renders in page content and needs an extra pin offset while acting.
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'));
  const bannerRef = useRef<HTMLDivElement>(null);
  const bannerAnchorDocTopRef = useRef<number>(UNINITIALIZED_POSITION);
  // Cached so scroll handlers avoid getComputedStyle on every tick.
  const isContainedScrollRef = useRef(false);
  const [isFixed, setIsFixed] = useState(false);
  const [bannerHeight, setBannerHeight] = useState<number>(0);
  const [bannerWidth, setBannerWidth] = useState<number>(0);
  const [bannerLeft, setBannerLeft] = useState<number>(0);

  const topOffset =
    isAdvisorActing && isMobileViewport
      ? GLOBAL_HEADER_HEIGHT + VIEW_AS_MOBILE_BANNER_HEIGHT
      : GLOBAL_HEADER_HEIGHT;

  /* Aligns banner width/left with tableRef. Pins when the banner’s top edge reaches the calculated top offset */
  useEffect(() => {
    if (!isOpen || disableViewportSticky) {
      return;
    }

    const table = tableRef.current;
    if (!table) {
      return;
    }

    const syncLayoutFromTable = () => {
      const rect = table.getBoundingClientRect();
      setBannerWidth((prev) =>
        Math.abs(prev - rect.width) < 0.5 ? prev : rect.width
      );
      setBannerLeft((prev) =>
        Math.abs(prev - rect.left) < 0.5 ? prev : rect.left
      );
    };

    const syncBannerHeight = () => {
      if (bannerRef.current) {
        setBannerHeight(bannerRef.current.getBoundingClientRect().height);
      }
    };

    const refreshContainedScroll = () => {
      isContainedScrollRef.current = hasContainedTableScroll(table);
    };

    const computePinned = (): boolean => {
      const bannerEl = bannerRef.current;
      if (!bannerEl) {
        return false;
      }

      const scrollTop =
        window.scrollY !== undefined
          ? window.scrollY
          : document.documentElement.scrollTop;

      // Inline style only — avoid getComputedStyle on every scroll tick.
      const layoutFixed = bannerEl.style.position === 'fixed';

      if (layoutFixed) {
        const anchor = bannerAnchorDocTopRef.current;
        return (
          anchor !== UNINITIALIZED_POSITION && scrollTop + topOffset >= anchor
        );
      }

      const rect = bannerEl.getBoundingClientRect();
      bannerAnchorDocTopRef.current = rect.top + scrollTop;
      return rect.top <= topOffset;
    };

    /** Scroll path: only update pin state; CSS var syncs via the isFixed effect. */
    const updatePinnedState = () => {
      setIsFixed(computePinned());
    };

    /** Layout/resize path: refresh contained-scroll cache and sync sticky var. */
    const onTableLayoutChange = () => {
      syncLayoutFromTable();
      syncBannerHeight();
      refreshContainedScroll();

      const bannerEl = bannerRef.current;
      const pinned = computePinned();
      setIsFixed(pinned);

      const height = bannerEl?.getBoundingClientRect().height ?? 0;
      syncStickyHeaderTopVar({
        table,
        isPinned: pinned && height > 0,
        topOffset,
        bannerHeight: height,
        isContainedScroll: isContainedScrollRef.current,
      });
    };

    const resizeObserver = new ResizeObserver(onTableLayoutChange);
    resizeObserver.observe(table);
    onTableLayoutChange();

    window.addEventListener('resize', onTableLayoutChange);
    window.addEventListener('scroll', updatePinnedState, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', onTableLayoutChange);
      window.removeEventListener('scroll', updatePinnedState);
      table.style.removeProperty(TABLE_STICKY_HEADER_TOP_VAR);
    };
  }, [isOpen, tableRef, topOffset, disableViewportSticky]);

  // Banner height when content or layout changes (e.g. CSV message).
  // biome-ignore lint/correctness/useExhaustiveDependencies: csvExport_showEntireTableMessage affects banner height
  useEffect(() => {
    if (isOpen && bannerRef.current) {
      const measure = () => {
        if (bannerRef.current) {
          setBannerHeight(bannerRef.current.getBoundingClientRect().height);
        }
      };
      measure();
      const timeoutId = setTimeout(measure, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, csvExportConfig?.showEntireTableMessage]);

  useEffect(() => {
    if (!isOpen) {
      setIsFixed(false);
      bannerAnchorDocTopRef.current = UNINITIALIZED_POSITION;
      setBannerHeight(0);
      setBannerWidth(0);
      setBannerLeft(0);
      syncStickyHeaderTopVar({
        table: tableRef.current,
        isPinned: false,
        topOffset,
        bannerHeight: 0,
        isContainedScroll: isContainedScrollRef.current,
      });
      return;
    }

    if (disableViewportSticky) {
      setIsFixed(false);
      bannerAnchorDocTopRef.current = UNINITIALIZED_POSITION;
      setBannerWidth(0);
      setBannerLeft(0);
      syncStickyHeaderTopVar({
        table: tableRef.current,
        isPinned: false,
        topOffset,
        bannerHeight: 0,
        isContainedScroll: isContainedScrollRef.current,
      });
    }
  }, [isOpen, disableViewportSticky, tableRef, topOffset]);

  // Keep sticky header offset in sync when pin state or banner height changes.
  useEffect(() => {
    const shouldPin =
      isOpen && !disableViewportSticky && isFixed && bannerHeight > 0;
    syncStickyHeaderTopVar({
      table: tableRef.current,
      isPinned: shouldPin,
      topOffset,
      bannerHeight,
      isContainedScroll: isContainedScrollRef.current,
    });
  }, [
    isOpen,
    disableViewportSticky,
    isFixed,
    bannerHeight,
    topOffset,
    tableRef,
  ]);

  const canSelectEntireTable = csvExportConfig?.maxSelectableRows
    ? selectedCounter < csvExportConfig.maxSelectableRows
    : false;

  const renderActions = (
    action: BannerAction,
    classes: Record<string, string>
  ) => {
    switch (action.type) {
      case 'label':
        return renderLabel(action);
      case 'custom':
        return action.render?.() ?? null;
      default:
        return renderButtonAction(action, classes);
    }
  };

  const {
    isAllSelected = false,
    onSelectAll,
    totalCount = 0,
    selectAllTranslationKey = 'common:selectAllVehicles',
  } = selectAllConfig || {};

  const handleSelectClick = isAllSelected ? onCloseBanner : onSelectAll;
  const selectButtonTranslationKey = isAllSelected
    ? 'common:clearSelection'
    : selectAllTranslationKey;
  const selectButtonTestId = isAllSelected
    ? 'clear-selection'
    : 'select-all-items';

  return (
    <Collapse in={isOpen}>
      {isLoading ? (
        <Skeleton variant="rectangular" height={BANNER_SKELETON_HEIGHT} />
      ) : (
        <>
          {!disableViewportSticky && isFixed && (
            <div style={{ height: `${bannerHeight}px` }} aria-hidden="true" />
          )}

          <div
            ref={bannerRef}
            className={!disableViewportSticky && isFixed ? 'banner-fixed' : ''}
            style={
              !disableViewportSticky && isFixed
                ? {
                    position: 'fixed',
                    top: `${topOffset}px`,
                    width: `${bannerWidth}px`,
                    left: `${bannerLeft}px`,
                    zIndex: SELECTED_ROW_ACTION_BANNER_Z_INDEX,
                    boxSizing: 'border-box',
                  }
                : {
                    width: '100%',
                    boxSizing: 'border-box',
                  }
            }
          >
            <div className={classes.tableBanner} data-testid="table-banner">
              <Flex
                gap={20}
                className={clsx(classes.tableBannerInner, {
                  'banner-fixed': !disableViewportSticky && isFixed,
                })}
              >
                <Flex className={classes.flexAlignCenter} gap={20}>
                  <Flex className="select-counters-wrapper" gap={20}>
                    <TranslatedLabel
                      className="selected-count"
                      dataTestId="selected-count"
                      translationKey="common:selectedCount"
                      typographyVariant="small_body_medium"
                      variables={{
                        count: selectedCounter,
                        formattedCount:
                          formattedSelectedCount ?? selectedCounter,
                      }}
                      style={{ whiteSpace: 'nowrap' }}
                    />
                    {selectAllConfig && (
                      <Button
                        variant="button-link"
                        size="large"
                        onClick={handleSelectClick}
                        className={`${classes.bannerActionLink} ${classes.selectAllButton}`}
                        data-testid={selectButtonTestId}
                      >
                        <TranslatedLabel
                          translationKey={selectButtonTranslationKey}
                          typographyVariant="small_body_medium"
                          variables={{ count: totalCount }}
                        />
                      </Button>
                    )}

                    {canSelectEntireTable && (
                      <Button
                        variant="button-link"
                        onClick={csvExportConfig?.onSelectAll}
                        className={classes.selectAllButton}
                      >
                        <TranslatedLabel
                          className="select-more-rows"
                          dataTestId="select-more-rows"
                          translationKey="common:csvExport.selectAllCount"
                          variables={{
                            count: csvExportConfig?.maxSelectableRows,
                          }}
                          typographyVariant="small_body_medium"
                          style={{ whiteSpace: 'nowrap' }}
                        />
                      </Button>
                    )}
                  </Flex>
                  {actions && (
                    <>
                      <Divider
                        className={classes.divider}
                        orientation="vertical"
                        flexItem
                      />

                      <Flex gap={20} className="actions-wrapper">
                        {actions?.map((action) => {
                          return (
                            <React.Fragment key={action.key}>
                              {renderActions(action, classes)}
                            </React.Fragment>
                          );
                        })}
                      </Flex>
                    </>
                  )}
                </Flex>
                {onCloseBanner && (
                  <IconButton
                    data-testid="banner-action-close"
                    className="action-close"
                    icon={<CloseIcon />}
                    onClick={onCloseBanner}
                    aria-label="Clear selection"
                  />
                )}
              </Flex>
            </div>
            {csvExportConfig?.showEntireTableMessage && (
              <div className={classes.messageContainer}>
                <InfoOutlinedIcon className={classes.infoIcon} />
                <TranslatedLabel
                  className={classes.entireTableMessage}
                  dataTestId="entire-table-message"
                  translationKey="common:csvExport.entireTableSelectedMessage"
                  typographyVariant="small_body_regular"
                />
              </div>
            )}
          </div>
        </>
      )}
    </Collapse>
  );
};

export default SelectedRowActionsBanner;
